<template>
  <Sidebar :collapsible="isMobile ? 'offcanvas' : 'icon'" variant="floating">
    <SidebarHeader class="p-1.5 gap-0">
      <div
        class="flex items-center justify-center h-12"
        :class="{
          'flex-row items-center justify-between': open || isMobile,
          'px-3': isMobile,
          'px-5 py-3': open,
          'flex-col gap-3 py-3': !open && !isMobile,
        }"
      >
        <NuxtLink to="/" @click="openMobile = false">
          <div
            class="flex items-center justify-center gap-1 text-white transition-all duration-1000 ease-in-out"
          >
            <LogoSquare
              class="transition-all duration-300 ease-in-out"
              :class="{ 'w-6 h-6': open }"
            />

            <LogoText
              v-show="open"
              class="transition-all duration-1000 ease-linear"
              :class="{
                'w-4 h-[26px] opacity-0': !open,
                'block opacity-100 h-6 !w-9': open,
              }"
            />
          </div>
        </NuxtLink>

        <SidebarTrigger
          :disabled="!loggedIn"
          class="bg-custom-gray-800 lg:bg-transparent lg:hidden relative h-10 w-10 z-[999999999]"
        />
      </div>

      <Button
        variant="outline"
        :size="open || isMobile ? 'lg' : 'icon'"
        class="relative mt-1 overflow-hidden transition-all duration-500 ease-out transform-gpu"
        :class="{
          'mx-auto w-10 h-10 rounded-full': !open,
          'mx-0 mb-2 mr-1 rounded-lg w-full': open || isMobile,
          'max-w-[300px] mx-auto': isMobile,
        }"
        style="
          transition:
            all 0.5s ease-out,
            border-radius 0.5s ease-out;
        "
        @click="createNewChat"
      >
        <Transition
          enter-active-class="transition-opacity duration-300 ease-out delay-200"
          leave-active-class="transition-opacity duration-150 ease-in"
          enter-from-class="opacity-0"
          enter-to-class="opacity-100"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <ProseSmall v-if="open || isMobile" class="whitespace-nowrap">
            {{ $t("common.create") }}
          </ProseSmall>
        </Transition>

        <Transition
          enter-active-class="transition-opacity duration-300 ease-out delay-200"
          leave-active-class="transition-opacity duration-150 ease-in"
          enter-from-class="opacity-0"
          enter-to-class="opacity-100"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <Plus
            v-if="!open && !isMobile"
            class="absolute inset-0 w-4 h-4 m-auto"
          />
        </Transition>
      </Button>
    </SidebarHeader>

    <Transition
      name="fade"
      mode="out-in"
      enter-active-class="transition-opacity duration-800"
      leave-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <SidebarContent v-if="(open && !isMobile) || (openMobile && isMobile)">
        <SidebarGroup>
          <SidebarGroupLabel
            class="pl-3 text-sm font-semibold leading-none lg:pl-2 text-custom-gray-150"
            >{{ $t("common.chats") }}</SidebarGroupLabel
          >
          <SidebarGroupContent>
            <SidebarMenu class="gap-1 mt-1">
              <SidebarMenuItem
                v-for="chat in chats"
                :key="chat.id"
                @mouseover="showMenuTrigger(chat.id)"
                @mouseleave="hideMenuTrigger(chat.id)"
                @click="loadChat(chat.id)"
              >
                <SidebarMenuButton as-child>
                  <Button
                    variant="ghost"
                    class="relative justify-start w-full px-3 py-4 text-base leading-tight sm:text-sm text-custom-gray-50 lg:px-2 lg:py-2 lg:text-sm hover:bg-custom-gray-700 focus:bg-custom-gray-700 active:bg-custom-gray-700"
                  >
                    <template v-if="renamingChatId === chat.id">
                      <input
                        v-model="editedTitle"
                        class="w-full bg-transparent border-b outline-none border-muted"
                        autofocus
                        @keydown.enter.prevent="saveRename(chat.id)"
                        @keydown.esc="cancelRename"
                        @blur="saveRename(chat.id)"
                      />
                    </template>
                    <template v-else>
                      {{ chat.title }}
                    </template>
                    <DropdownMenu
                      @update:open="
                        (isOpen) =>
                          (openDropdownChatId = isOpen ? chat.id : null)
                      "
                    >
                      <DropdownMenuTrigger
                        as-child
                        :class="{
                          'opacity-100': menuTriggerShown(chat.id) || isMobile,
                          'opacity-0 pointer-events-none':
                            !menuTriggerShown(chat.id) && !isMobile,
                        }"
                        @click.stop
                      >
                        <SidebarMenuAction
                          class="absolute -translate-y-1/2 right-2 top-1/2"
                        >
                          <Ellipsis class="w-4 h-4 rounded-2xl" />
                        </SidebarMenuAction>
                      </DropdownMenuTrigger>

                      <DropdownMenuContent side="right" align="start">
                        <DropdownMenuItem @click="handleRename(chat.id)"
                          ><ProseSmall>{{
                            $t("common.rename")
                          }}</ProseSmall></DropdownMenuItem
                        >
                        <DropdownMenuItem
                          :disabled="false"
                          class="hover:!bg-destructive"
                          @click="handleDeleteChat(chat.id)"
                        >
                          <ProseSmall class="!text-destructive-foreground">{{
                            $t("common.delete")
                          }}</ProseSmall>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Button>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarContent v-else />
    </Transition>

    <SidebarFooter class="p-1.5 mt-1">
      <Button
        size="icon"
        class="w-10 h-10"
        variant="outline"
        @click="handleOpenFeedbackModal"
      >
        ?
      </Button>
    </SidebarFooter>
  </Sidebar>
</template>

<script lang="ts" setup>
import { Ellipsis, Plus } from "lucide-vue-next";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import ProseSmall from "../Typography/ProseSmall.vue";
import LogoText from "./LogoText.vue";
import LogoSquare from "./LogoSquare.vue";

const { openFeedbackModal } = useModals();

const { isMobile, openMobile, open } = useSidebar();
const { createChat } = useAIChat();
const { fetchChats, chats, deleteChat, renameChat } = useAIChat();
const { uuid } = useAIUuid();
const { openSignInModal } = useModals();
const { status } = useAuth();
const loggedIn = computed(() => status.value === "authenticated");

const { t } = useI18n();

const hoveredChatId = ref<string | null>(null);
const openDropdownChatId = ref<string | null>(null);
const renamingChatId = ref<string | null>(null);
const editedTitle = ref<string>("");

onMounted(async () => {
  if (!loggedIn.value) {
    return;
  }
  await fetchChats();
});

const loadChat = (id: string) => {
  if (renamingChatId.value) {
    return;
  }

  if (isMobile.value) {
    openMobile.value = false;
  }

  const router = useRouter();

  router.push("/chat/" + id);
};

const handleDeleteChat = async (id: string) => {
  if (!confirm(t("chat.delete_chat_confirmation"))) {
    return;
  }

  await deleteChat(id);
  if (uuid.value === id) {
    const router = useRouter();
    router.push("/");
  }
  await fetchChats();
};

const handleOpenFeedbackModal = () => {
  if (isMobile.value) {
    openMobile.value = false;
  }

  if (!loggedIn.value) {
    openSignInModal();
    return;
  }

  openFeedbackModal();
};

const createNewChat = async () => {
  if (!loggedIn.value) {
    openSignInModal();
    return;
  }

  const router = useRouter();

  const chat = await createChat();

  if (!chat) {
    return;
  }

  router.push("/chat/" + chat.id);
};

const showMenuTrigger = (id: string) => {
  hoveredChatId.value = id;
};

const hideMenuTrigger = (id: string) => {
  if (openDropdownChatId.value !== id) {
    hoveredChatId.value = null;
  }
};

const menuTriggerShown = (id: string) => {
  return hoveredChatId.value === id || openDropdownChatId.value === id;
};

const handleRename = (chatId: string) => {
  const chat = chats.value.find((c) => c.id === chatId);
  if (chat) {
    renamingChatId.value = chatId;
    editedTitle.value = chat.title;
  }
};

const saveRename = async (chatId: string) => {
  const chat = chats.value.find((c) => c.id === chatId);
  if (!chat || chat.title === editedTitle.value.trim()) {
    renamingChatId.value = null;
    return;
  }

  try {
    await renameChat(chatId, editedTitle.value.trim());
    await fetchChats();
  } catch (e) {
    console.error("Failed to rename chat:", e);
  } finally {
    renamingChatId.value = null;
  }
};

const cancelRename = () => {
  renamingChatId.value = null;
  editedTitle.value = "";
};
</script>

<style scoped>
.fade-enter-active {
  transition: opacity 1s;
}

.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}
</style>
